<template>
    <LoginDialog @close="close" :withoutClose="withoutClose" :title="dialogTitleText">
        <div class="px-10 py-4 flex flex-col">
            <SignUp v-if="showRegister" :channels="channels" class="mb-2"/>
            <template v-else>
                <GoogleSignInButton class="mb-4"/>
                <LinkedInSignInButton />
                <!-- 开发环境使用本地表单 -->
                <LocalLoginForm v-if="isDevelopment" @success="handleLoginSuccess" />
                <!-- 生产环境使用WujieVue -->
                <WujieVue v-else v-bind="wujieOptions" class="mb-4 border-b border-grey-40 pb-4" :plugins="wujiePlugins"/>
            </template>

            <div class="flex justify-center items-center text-grey-90 text-sm">
                {{ tipText }}
                <button class="th-btn th-btn-text" @click="toggleDialogMode">
                    {{ buttonText }}
                </button>
            </div>
        </div>
    </LoginDialog>
</template>

<script setup lang="ts">
import SignUp from './login-us/index.vue'
import LoginDialog from './LoginDialog.vue';
import LocalLoginForm from './login-us/components/LocalLoginForm.vue';
import { SLD, Site } from '@patsnap-utils/biz';
import WujieVue from 'wujie-vue3';
import { type Channels, DialogMode, WUJIE_PROPS_USER_ENTRY_METHOD } from '..';
import { buildQueryChannels } from '..';
import { useI18n } from 'vue-i18n';
import { useChannels } from './login-us/composables/use-channels';
import { computed, onMounted } from 'vue';
import LinkedInSignInButton from './login-us/components/LinkedInSignInButton.vue';
import GoogleSignInButton from './login-us/components/GoogleSignInButton.vue';
import { getVar } from './login-us/utils/get-register-url-params';
import { useLocalStorage } from '@vueuse/core'
import type { InitFormParams } from './login-us/types/accounts';
const { t } = useI18n()

// 判断是否为开发环境
const isDevelopment = computed(() => {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
})

const cacheDialogModeKey = 'login-dialog-mode'

const props = defineProps<{
    channels?: Channels,
    withoutClose?: boolean,
    initFormParams?: InitFormParams,
    isSignup?: boolean,
}>()

const dialogMode = useLocalStorage(cacheDialogModeKey, DialogMode.REGISTER)

dialogMode.value = props.isSignup ? DialogMode.REGISTER : DialogMode.LOGIN

const emits = defineEmits<{
    (e: 'success', data: {[key: string]: string}): void;
    (e: 'failed', error: Error): void;
    (e: 'close'): void;
}>();

const buildQueryParams = computed(() => {
    return {
        ...props.channels,
        ...(getVar('debug_region') === 'us' ? { region: 'us' } : {}),
    }
})

/**
* 处理跳转到忘记密码页面
*/
function isForgetPassword(url: string) {
    const forgetPsdLabel = 'forgetPassword'
    return url.includes(forgetPsdLabel)
}

/**
 * 处理US SSO链接
 */
function isSSOLink(url: string) {
    const SSO_URI = new Site({ sld: SLD.PASSPORT, pathname: 'saml' }).href.replace(/^https?:\/\//, '')
    const normalizedUrl = url.replace(/^https?:\/\//, '')
    return new RegExp(`^${SSO_URI}`).test(normalizedUrl)
}

const wujieOptions = {
    name: 'eureka:air-login',
    // url: new Site({ sld: SLD.ACCOUNT, pathname: `?${buildQueryChannels(buildQueryParams.value)}/#/sub-login` }).href,
    url: `${import.meta.env.VITE_ACCOUNT_URI}/login/?${buildQueryChannels(props.channels)}/#/sub-login`,
    props: {
        userEntryMethods: [WUJIE_PROPS_USER_ENTRY_METHOD.Account],
        jumpTo: (url: string) => {
            if (isForgetPassword(url) || isSSOLink(url)) {
                window.location.replace(url)
                return
            }
            emits('success', { url })
        },
        loginFormParams: props.initFormParams,
    },
}

const wujiePlugins = [
    {
        cssAfterLoaders: [
            { content: '.idps-suggestion{left:0!important;}' }
        ],
    }
]

const { updateChannels } = useChannels()

const showRegister = computed(() => dialogMode.value === DialogMode.REGISTER)

const tipText = computed(() => dialogMode.value === DialogMode.REGISTER ? t('us.already_have_account') : t('us.not_have_account'))

const buttonText = computed(() => dialogMode.value === DialogMode.REGISTER ? t('us.login') : t('us.register'))

const dialogTitleText = computed(() => dialogMode.value === DialogMode.REGISTER ? t('us.free_sign_up') : t('us.login'))

function toggleDialogMode() {
    dialogMode.value = dialogMode.value === DialogMode.REGISTER ? DialogMode.LOGIN : DialogMode.REGISTER
}

function close() {
    emits('close')
}

// 处理本地Mock登录成功
function handleLoginSuccess(data: { token: string; user: any }) {
    console.log('登录成功:', data)
    // 模拟跳转URL
    emits('success', { url: window.location.href })
}

onMounted(() => {
    if (props.channels) {
        updateChannels(props.channels)
    }
})

</script>
